import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { isValidCpf } from '@/shared/lib/cpf';
import { maskBirthday, maskCpf, maskPhone } from '@/shared/lib/input-masks';
import { onlyDigits } from '@/shared/lib/format';
import { registerInfluencer } from '../api/use-influencers';

/** Mesmos valores do cadastro público — legacy/fixe-link-2 views/invite.ejs. */
const GENDER_OPTIONS = [
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Feminino' },
  { value: 'O', label: 'Outros' },
  { value: 'P', label: 'Prefiro não dizer' },
] as const;

/** Só formato — igual ao cadastro público, não valida data real. */
const BIRTHDAY_RE = /^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/;

const schema = z.object({
  name: z.string().trim().min(1, 'Digite o nome'),
  email: z.string().trim().refine((v) => v === '' || z.string().email().safeParse(v).success, 'O email é inválido'),
  phone: z.string().refine((v) => onlyDigits(v).length >= 10, 'Digite um telefone válido'),
  cpf: z.string().refine((v) => isValidCpf(v), 'O CPF é inválido'),
  birthday: z.string().refine((v) => v === '' || BIRTHDAY_RE.test(v), 'Data inválida (DD/MM/AAAA)'),
  gender: z.string(),
  defaultBalance: z
    .string()
    .refine((v) => v === '' || (Number(v) >= 0 && Number.isFinite(Number(v))), 'Digite um número válido'),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  name: '',
  email: '',
  phone: '',
  cpf: '',
  birthday: '',
  gender: '',
  defaultBalance: '',
};

interface Props {
  idPassbook: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function InfluencerFormDialog({ idPassbook, open, onOpenChange, onSaved }: Props) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (open) form.reset(EMPTY);
  }, [open, form]);

  const submit = form.handleSubmit(async (values) => {
    try {
      await registerInfluencer({
        idPassbook,
        name: values.name,
        email: values.email,
        phone: values.phone,
        cpf: values.cpf,
        birthday: values.birthday,
        gender: values.gender,
        defaultBalance: values.defaultBalance === '' ? undefined : Number(values.defaultBalance),
      });
      toast.success('Parceiro cadastrado.');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao cadastrar o parceiro.');
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Cadastrar parceiro</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="grid gap-4">
          <Field label="Nome" error={form.formState.errors.name?.message}>
            <Input placeholder="Nome do parceiro" {...form.register('name')} />
          </Field>

          <Field label="Email" error={form.formState.errors.email?.message}>
            <Input type="email" placeholder="email@exemplo.com" {...form.register('email')} />
          </Field>

          <Field label="Telefone" error={form.formState.errors.phone?.message}>
            <Input
              placeholder="(11) 99999-9999"
              inputMode="numeric"
              {...form.register('phone')}
              onChange={(e) => form.setValue('phone', maskPhone(e.target.value), { shouldValidate: true })}
            />
          </Field>

          <Field label="CPF" error={form.formState.errors.cpf?.message}>
            <Input
              placeholder="000.000.000-00"
              inputMode="numeric"
              {...form.register('cpf')}
              onChange={(e) => form.setValue('cpf', maskCpf(e.target.value), { shouldValidate: true })}
            />
          </Field>

          <Field label="Data de nascimento" error={form.formState.errors.birthday?.message}>
            <Input
              placeholder="DD/MM/AAAA"
              inputMode="numeric"
              {...form.register('birthday')}
              onChange={(e) => form.setValue('birthday', maskBirthday(e.target.value), { shouldValidate: true })}
            />
          </Field>

          <Field label="Gênero" error={form.formState.errors.gender?.message}>
            <Select
              value={form.watch('gender') || undefined}
              onValueChange={(v) => form.setValue('gender', v, { shouldValidate: true })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o gênero" />
              </SelectTrigger>
              <SelectContent>
                {GENDER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field
            label="Saldo inicial de selos"
            error={form.formState.errors.defaultBalance?.message}
            hint="Deixe em branco para cadastrar sem crédito."
          >
            <Input type="number" min={0} step={1} placeholder="0" {...form.register('defaultBalance')} />
          </Field>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Cadastrando...' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
      {hint && !error ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
